import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../../api.ts';
import { TrustBadges, type Badges } from '../../../components/TrustBadges.tsx';
import { fmtDay, type Client } from './types.ts';
import { groupBy, ofKind, useCatalog } from '../../../catalog.ts';

interface Reference {
  id: string;
  kind: 'EMPLOYER' | 'CLIENT';
  name: string;
  companyAddress: string | null;
  firstName: string | null;
  lastName: string | null;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  channel: 'EMAIL' | 'SMS' | 'BOTH';
  roleTitle: string | null;
  status: 'REQUESTED' | 'CONFIRMED' | 'DECLINED' | 'EXPIRED';
  sent: boolean;
  expiresAt: string;
  respondedAt: string | null;
}

interface Vigilance {
  id: string;
  fileName: string;
  status: 'PENDING' | 'VERIFIED' | 'REJECTED';
  uploadedAt: string;
  expiresAt: string | null;
  rejectionReason: string | null;
  hasFile: boolean;
}

interface Data {
  siret: { siret: string | null; status: 'UNCHECKED' | 'ACTIVE' | 'CEASED' | 'NOT_FOUND' | 'NAME_MISMATCH' | 'MANUAL_OK'; checkedAt: string | null; legalName: string | null };
  references: Reference[];
  limits: { maxReferences: number; validityDays: number };
  experience: { required: number; declared: number };
  delivery: { email: boolean; sms: boolean };
  vigilance: Vigilance[];
  vigilanceValidityMonths: number;
  badges: Badges;
}

const SIRET_STATUS: Record<Data['siret']['status'], { label: string; badge: string; help: string }> = {
  UNCHECKED: { label: 'Non vérifié', badge: 'badge-neutral', help: 'Renseignez votre SIRET dans l’onglet « Facturation » : il est contrôlé automatiquement.' },
  ACTIVE: { label: 'Actif', badge: 'badge-available', help: 'Votre établissement est actif dans la base officielle des entreprises et correspond à votre nom.' },
  MANUAL_OK: { label: 'Validé par l’équipe', badge: 'badge-available', help: 'Votre SIRET a été validé manuellement par l’équipe Mon Extra.' },
  CEASED: { label: 'Cessé', badge: 'badge-unavailable', help: 'Ce SIRET apparaît comme cessé. Vérifiez votre situation auprès de l’URSSAF ou de l’INSEE.' },
  NOT_FOUND: { label: 'Introuvable', badge: 'badge-pending', help: 'Ce SIRET ne ressort pas dans la base officielle : vérifiez-le. Si votre entreprise est non diffusible, l’équipe pourra le valider à la main.' },
  NAME_MISMATCH: { label: 'Nom différent', badge: 'badge-pending', help: 'Le nom associé à ce SIRET ne correspond pas à votre compte. Vérifiez le numéro, ou contactez-nous si c’est une situation normale.' },
};

const REF_STATUS: Record<Reference['status'], { label: string; badge: string }> = {
  REQUESTED: { label: 'En attente', badge: 'badge-pending' },
  CONFIRMED: { label: 'Confirmée', badge: 'badge-available' },
  DECLINED: { label: 'Sans suite', badge: 'badge-neutral' },
  EXPIRED: { label: 'Expirée', badge: 'badge-unavailable' },
};

const VIGILANCE_STATUS: Record<Vigilance['status'], { label: string; badge: string }> = {
  PENDING: { label: 'En cours de contrôle', badge: 'badge-pending' },
  VERIFIED: { label: 'Vérifiée', badge: 'badge-available' },
  REJECTED: { label: 'Refusée', badge: 'badge-unavailable' },
};

const ERRORS: Record<string, string> = {
  REFEREE_NOT_INFORMED: 'Confirmez que vous avez prévenu cette personne.',
  INVALID_EMAIL: 'Adresse e-mail invalide.',
  INVALID_PHONE: 'Numéro de téléphone invalide (ex. 06 12 34 56 78 ou +33 6 12 34 56 78).',
  CONTACT_REQUIRED: 'Indiquez au moins une adresse e-mail ou un numéro de téléphone.',
  EMAIL_REQUIRED_FOR_CHANNEL: 'Ajoutez une adresse e-mail pour un envoi par e-mail.',
  PHONE_REQUIRED_FOR_CHANNEL: 'Ajoutez un numéro de téléphone pour un envoi par SMS.',
  INVALID_ROLE: 'Poste inconnu.',
  MISSING_FIELDS: 'Le prénom, le nom, l’entreprise et son adresse sont obligatoires.',
  SELF_REFERENCE: 'Vous ne pouvez pas vous désigner vous-même.',
  DUPLICATE_REFERENCE: 'Vous avez déjà demandé une référence à cette personne.',
  TOO_MANY_REFERENCES: 'Vous avez atteint le nombre maximal de références. Retirez-en une pour en ajouter une autre.',
  RESEND_TOO_SOON: 'Un message a déjà été envoyé il y a moins de 24 heures.',
  CHECK_TOO_SOON: 'Patientez une minute avant de relancer la vérification.',
  SIRET_LOOKUP_UNAVAILABLE: 'La base officielle ne répond pas pour le moment. Réessayez plus tard.',
  SIRET_MISSING: 'Renseignez d’abord votre SIRET dans l’onglet « Facturation ».',
  INVALID_FILE: 'Le fichier doit être un PDF.',
  FILE_TOO_LARGE: 'Le fichier dépasse 5 Mo.',
};

const msg = (err: unknown, fallback: string) => (err instanceof ApiError ? ERRORS[err.code] ?? fallback : fallback);

export function Verifications() {
  const [data, setData] = useState<Data | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const emptyForm = { clientId: '', name: '', companyAddress: '', firstName: '', lastName: '', email: '', phone: '', channel: 'AUTO' as 'AUTO' | 'EMAIL' | 'SMS' | 'BOTH', roleItemId: '', roleTitle: '', periodLabel: '', informed: false };
  const [form, setForm] = useState(emptyForm);
  // Lien de la dernière demande : rendu une seule fois par le serveur, que l'extra peut transmettre lui-même.
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const catalog = useCatalog();
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    api.get<Data>('/extra/verifications').then(setData);
    api.get<{ clients: Client[] }>('/extra/clients').then((r) => setClients(r.clients));
  }, []);
  useEffect(load, [load]);

  async function wrap(action: () => Promise<unknown>, fallback: string, success?: string) {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await action();
      if (success) setNote(success);
      load();
    } catch (err) {
      setError(msg(err, fallback));
    } finally {
      setBusy(false);
    }
  }

  function pickClient(id: string) {
    const c = clients.find((x) => x.id === id);
    setForm((f) => ({ ...f, clientId: id, ...(c ? { name: c.name, email: c.email ?? f.email } : {}) }));
  }

  async function submitReference(e: FormEvent) {
    e.preventDefault();
    await wrap(
      async () => {
        const res = await api.post<{ shareUrl: string }>('/extra/verifications/references', {
          kind: form.clientId ? 'CLIENT' : 'EMPLOYER',
          clientId: form.clientId || undefined,
          name: form.name,
          companyAddress: form.companyAddress,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          channel: form.channel === 'AUTO' ? undefined : form.channel,
          roleItemId: form.roleItemId || undefined,
          roleTitle: form.roleTitle || undefined,
          periodLabel: form.periodLabel || undefined,
          informedReferee: form.informed,
        });
        setShareUrl(res.shareUrl);
        setCopied(false);
        setForm(emptyForm);
        setOpen(false);
      },
      'Demande impossible.',
      'Demande enregistrée.',
    );
  }

  async function copyLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      window.prompt('Copiez ce lien :', url);
    }
  }

  async function upload() {
    const file = fileInput.current?.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    await wrap(
      async () => {
        const res = await fetch('/api/extra/verifications/vigilance', { method: 'POST', body, credentials: 'include' });
        if (!res.ok) throw new ApiError((await res.json().catch(() => null))?.error ?? 'REQUEST_FAILED', res.status);
      },
      'Envoi impossible.',
      'Attestation envoyée : elle sera contrôlée par l’équipe.',
    );
    if (fileInput.current) fileInput.current.value = '';
  }

  if (!data) return <div className="empty-state">Chargement…</div>;
  const siret = SIRET_STATUS[data.siret.status];
  const activeRefs = data.references.filter((r) => r.status === 'REQUESTED' || r.status === 'CONFIRMED').length;

  return (
    <>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        Ces vérifications rassurent les établissements : ils voient des badges, jamais vos coordonnées ni les réponses de vos références.
      </div>
      <TrustBadges badges={data.badges} />
      {error && <div className="error-banner">{error}</div>}
      {note && <div className="badge badge-available" style={{ padding: '10px 14px' }}>{note}</div>}

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div style={{ fontWeight: 800 }}>Mon SIRET</div>
          <span className={`badge ${siret.badge}`}>{siret.label}</span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          {data.siret.siret ? <><strong style={{ color: 'var(--text)' }}>{data.siret.siret}</strong>{data.siret.legalName ? ` · ${data.siret.legalName}` : ''}<br /></> : null}
          {siret.help}
          {data.siret.checkedAt && ` Dernier contrôle le ${fmtDay(data.siret.checkedAt)} ; il est refait chaque mois.`}
        </div>
        {data.siret.siret && (
          <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} disabled={busy} onClick={() => wrap(() => api.post('/extra/verifications/siret/check'), 'Vérification impossible.')}>
            Relancer la vérification
          </button>
        )}
      </div>

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontWeight: 800 }}>Mes références</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          Indiquez jusqu'à {data.limits.maxReferences} anciens employeurs ou clients. Chaque référent reçoit un court questionnaire (e-mail ou SMS, {data.limits.validityDays} jours pour répondre)
          où il note vos compétences sur 5 : vous ne voyez que son état, pas ses réponses. Une note globale de référence en découle.
        </div>
        {data.experience.required > 0 && (
          <div className={data.experience.declared >= data.experience.required ? 'badge badge-available' : 'badge badge-pending'} style={{ padding: '10px 14px', whiteSpace: 'normal', lineHeight: 1.5 }}>
            {data.experience.declared >= data.experience.required
              ? 'Vous avez déclaré l’expérience demandée : vous pouvez candidater.'
              : `Pour candidater à une mission, déclarez au moins ${data.experience.required} expérience${data.experience.required > 1 ? 's' : ''} avec un référent (${data.experience.declared}/${data.experience.required}).`}
          </div>
        )}
        {shareUrl && (
          <div className="card" style={{ background: 'var(--accent-tint)', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>Lien de la demande</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {data.delivery.email || data.delivery.sms
                ? 'La demande est envoyée automatiquement. Vous pouvez aussi transmettre ce lien vous-même (WhatsApp, SMS personnel…). Il n’est affiché qu’ici, une seule fois.'
                : 'L’envoi automatique n’est pas encore activé : transmettez ce lien vous-même à votre référent (WhatsApp, SMS personnel, e-mail). Il n’est affiché qu’ici, une seule fois.'}
            </div>
            <div style={{ fontSize: 12, overflowWrap: 'anywhere', fontFamily: 'monospace' }}>{shareUrl}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={() => copyLink(shareUrl)}>{copied ? 'Lien copié' : 'Copier le lien'}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setShareUrl(null)}>Masquer</button>
            </div>
          </div>
        )}
        {data.references.map((r) => (
          <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700 }}>{r.name}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>
                {r.kind === 'CLIENT' ? 'Client' : 'Employeur'}
                {(r.firstName || r.lastName) && ` · ${[r.firstName, r.lastName].filter(Boolean).join(' ')}`}
                {r.roleTitle && ` · ${r.roleTitle}`}
                {` · ${[r.email, r.phone].filter(Boolean).join(' / ')}`}
                {r.status === 'REQUESTED' && (r.sent ? ` · expire le ${fmtDay(r.expiresAt)}` : ' · lien à transmettre vous-même')}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
              <span className={`badge ${REF_STATUS[r.status].badge}`}>{REF_STATUS[r.status].label}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {(r.status === 'REQUESTED' || r.status === 'EXPIRED') && (
                  <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => wrap(async () => {
                    const res = await api.post<{ shareUrl: string }>(`/extra/verifications/references/${r.id}/resend`);
                    setShareUrl(res.shareUrl);
                    setCopied(false);
                  }, 'Envoi impossible.', 'Nouveau lien généré.')}>{r.status === 'EXPIRED' ? 'Relancer' : 'Renvoyer'}</button>
                )}
                <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => window.confirm(`Retirer « ${r.name} » ?`) && wrap(() => api.delete(`/extra/verifications/references/${r.id}`), 'Suppression impossible.')}>Retirer</button>
              </div>
            </div>
          </div>
        ))}
        {!open && activeRefs < data.limits.maxReferences && (
          <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setOpen(true)}>Ajouter une référence</button>
        )}
        {open && (
          <form onSubmit={submitReference} style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            {clients.length > 0 && (
              <select className="input" value={form.clientId} onChange={(e) => pickClient(e.target.value)} aria-label="Client du carnet">
                <option value="">Employeur ou client hors carnet…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            <input className="input" required placeholder="Nom de l’entreprise" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className="input" required placeholder="Adresse de l’entreprise" value={form.companyAddress} onChange={(e) => setForm({ ...form, companyAddress: e.target.value })} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <input className="input" required placeholder="Prénom du référent" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
              <input className="input" required placeholder="Nom du référent" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <input className="input" type="email" placeholder="Adresse e-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input className="input" type="tel" placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: -4 }}>Une adresse e-mail ou un numéro de téléphone suffit.</div>
            {form.email && form.phone && (
              <select className="input" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value as typeof form.channel })} aria-label="Canal d’envoi">
                <option value="AUTO">Envoyer par e-mail et par SMS</option>
                <option value="EMAIL">Envoyer par e-mail seulement</option>
                <option value="SMS">Envoyer par SMS seulement</option>
              </select>
            )}
            {catalog && (
              <select className="input" value={form.roleItemId} onChange={(e) => setForm({ ...form, roleItemId: e.target.value })} aria-label="Poste occupé">
                <option value="">Poste occupé (détermine les compétences notées)…</option>
                {groupBy(ofKind(catalog, 'ROLE'), (i) => i.group).map(([group, items]) => (
                  <optgroup key={group} label={group}>
                    {items.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
                  </optgroup>
                ))}
              </select>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <input className="input" placeholder="Intitulé du poste" value={form.roleTitle} onChange={(e) => setForm({ ...form, roleTitle: e.target.value })} />
              <input className="input" placeholder="Période (ex. 2024)" value={form.periodLabel} onChange={(e) => setForm({ ...form, periodLabel: e.target.value })} />
            </div>
            <label style={{ display: 'flex', gap: 8, fontSize: 12.5, lineHeight: 1.5, alignItems: 'flex-start' }}>
              <input type="checkbox" checked={form.informed} onChange={(e) => setForm({ ...form, informed: e.target.checked })} style={{ marginTop: 3 }} />
              J'ai prévenu cette personne qu'elle sera contactée par Mon Extra (e-mail ou SMS) et elle est d'accord pour être contactée.
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary btn-sm" disabled={busy || !form.informed}>Envoyer la demande</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(false)}>Annuler</button>
            </div>
          </form>
        )}
      </div>

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontWeight: 800 }}>Attestation de vigilance URSSAF</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          Certains clients la demandent. Téléchargez-la gratuitement sur autoentrepreneur.urssaf.fr, puis déposez le PDF (5 Mo maximum). L'équipe vérifie son code de sécurité
          sur le site de l'URSSAF ; elle est valable {data.vigilanceValidityMonths} mois. Le fichier n'est visible que de vous et de l'équipe.
        </div>
        {data.vigilance.map((v) => (
          <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{v.fileName}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                Déposée le {fmtDay(v.uploadedAt)}
                {v.expiresAt && ` · valable jusqu'au ${fmtDay(v.expiresAt)}`}
                {v.rejectionReason && ` · ${v.rejectionReason}`}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
              <span className={`badge ${VIGILANCE_STATUS[v.status].badge}`}>{VIGILANCE_STATUS[v.status].label}</span>
              {v.hasFile && <a className="btn btn-ghost btn-sm" href={`/api/extra/verifications/vigilance/${v.id}/file`}>Télécharger</a>}
            </div>
          </div>
        ))}
        <input ref={fileInput} type="file" accept="application/pdf" aria-label="Attestation de vigilance (PDF)" style={{ fontSize: 13 }} />
        <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-start' }} disabled={busy} onClick={upload}>Déposer l'attestation</button>
      </div>
    </>
  );
}
